import { APP_ROUTES, useAppTranslation } from "@/utils";
import { bnplConsumerApis } from "@payping/api";
import {
  Alert,
  Button,
  Card,
  Divider,
  Flex,
  Modal,
  Radio,
  Tag,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { useState } from "react";
import { moment } from "@payping/utils/dateUtils";
import { useNavigate } from "react-router";
import Loading from "@/pages/loading";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { PPFullColorLogoFa } from "@/core/utils";

interface PlanProps {
  trackingCode: string;
  planId: number;
}

const InprogressLimit = ({ planId, trackingCode }: PlanProps) => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [selectedTrackingCode, setSelectedTrackingCode] = useState(trackingCode);
  const [openModal, setOpenModal] = useState(false);

  const handleChange = (code: any) => {
    setSelectedTrackingCode(code);
  };

  const inprogressState = useMessagedAsync(() => bnplConsumerApis.getInProgressOrders(trackingCode), [trackingCode], {
    success: false,
    error: true,
  });

  const inprogressList = inprogressState.value || [];

  const [setOrderState, setOrder] = useMessagedAsyncFn(
    async (tc: string) => {
      try {
        const res = await bnplConsumerApis.chooseInProgressOrders({
          trackingCode: tc,
          planId,
        });

        navigate(`${APP_ROUTES.pay.getOrder.root.url}?trackingCode=${tc}`);
        return res;
      } catch (e: any) {
        // Check if e is an instance of Response and if the status is 400
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }

        throw e;
      }
    },
    [navigate, planId],
  );

  const handleCancelModal = () => {
    setOpenModal(false);
  };

  const handleShowModal = () => {
    if (selectedTrackingCode) {
      setOpenModal(true);
    }
  };

  if (inprogressState.loading) {
    return <Loading />;
  }

  return (
    <Flex vertical className="tw:h-full">
      <Flex className="tw:w-full tw:p-5!" align="center">
        <img src={PPFullColorLogoFa} width={60} height={24} />
      </Flex>
      <Divider className="tw:m-0!" />
      <Flex vertical className="tw:h-full tw:w-full tw:p-3!" gap="middle">
        <Alert title={null} description={t("pay.inprogressTitle")} type="warning" showIcon />
        <Flex className="tw:w-full" gap="middle" vertical>
          {inprogressList.map((item, index) => (
            <>
              <Card
                key={index}
                type="inner"
                title={
                  <Flex>
                    <Radio
                      checked={selectedTrackingCode === item.trackingCode}
                      onChange={() => handleChange(item.trackingCode)}
                    >
                      <Flex gap={"small"} align="center">
                        <Typography.Text type="secondary" className="tw:text-sm!">
                          {t("pay.purchaseCredit")}:
                        </Typography.Text>
                        <Flex align="center" gap={"2px"}>
                          <Typography.Text>{`${formatLocalizedNumber({ value: item.creditedAmount ?? 0 })}`}</Typography.Text>
                          <CuToman width={24} height={24} />
                        </Flex>
                      </Flex>
                    </Radio>
                  </Flex>
                }
                extra={item.isCurrent ? <Tag color="geekblue">{t("pay.currentPurchase")}</Tag> : null}
              >
                <Flex gap={"small"} justify="space-between">
                  <Typography.Text className="tw:text-sm!" type="secondary">
                    {t("pay.purchaseDeadline")}:
                  </Typography.Text>
                  <Flex gap={"small"}>
                    <Typography.Text className="tw:m-0!" type="secondary">
                      {t("pay.untilTime")}
                    </Typography.Text>
                    <Typography.Text className="tw:m-0! tw:text-color-primary">
                      {moment(item.expireDate).format("HH:mm")}
                    </Typography.Text>
                  </Flex>
                </Flex>
              </Card>
            </>
          ))}
        </Flex>
        <Typography.Text className="tw:text-sm!" type="secondary">
          {t("pay.expiredProcessNote")}
        </Typography.Text>
        <Button className="tw:mt-auto" disabled={!selectedTrackingCode} type="primary" block onClick={handleShowModal}>
          {t("common.continue")}
        </Button>
      </Flex>

      <Modal
        open={openModal}
        className="tw:max-w-[290px]"
        title={t("pay.continueProcess")}
        onCancel={handleCancelModal}
        footer={
          <Flex gap="small" justify="flex-end">
            <Button type="default" disabled={setOrderState.loading} onClick={handleCancelModal}>
              {t("pay.cancel")}
            </Button>
            <Button loading={setOrderState.loading} type="primary" onClick={() => setOrder(selectedTrackingCode)}>
              {t("common.confirmAndContinue")}
            </Button>
          </Flex>
        }
      >
        <Flex vertical justify="center" className="tw:py-4!">
          <Typography.Text className="tw:text-sm!">{t("pay.otherInstallmentCancelNote")}</Typography.Text>
          <Typography.Text className="tw:text-sm!">{t("pay.confirmContinue")}</Typography.Text>
        </Flex>
      </Modal>
    </Flex>
  );
};

export default InprogressLimit;
